import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { DeckIcon } from '~/design-system/elements/DeckIcon/DeckIcon';
import {
  FONT_CATALOG,
  filterFonts,
  getFontById,
  getFontByFamily,
  type FontCategoryFilter,
  type FontMeta,
} from '~/design-system/fonts/catalog';
import { fontHref, loadFont, loadFontByFamily } from '~/design-system/fonts/loadFont';
import { cn } from '~/lib/utils';
import themes from '~/design-system/components/v1_5/Header/HeaderTheme.module.css';
import styles from './TextFontPanel.module.css';
import type { HeaderTheme } from './theme';
import { useV15Theme } from '~/design-system/components/v1_5/theme-context';

/**
 * Fixed, like every other v1.5 menu width, and MEASURED rather than chosen: 288 is what the six
 * category chips need laid out in a row at their own widths, plus room to spare. It also gives a
 * display face more line to be judged on, which is the other thing this panel is for.
 */
export const TEXT_FONT_PANEL_WIDTH = 288;

/**
 * The windowing constants, ported from `Toolbar/PropertiesPanel/FontPicker` with its reasoning intact:
 * one uniform row height is what makes the list cheap to window, and the catalogue is ~1,900 families,
 * so windowing is not an optimisation — it is the difference between a list and a hang.
 */
const ROW_H = 30;
const OVERSCAN = 12;
/** Rows whose FACE is fetched beyond the rendered band, so a name is set in itself before you reach it. */
const PRELOAD = 32;
const LIST_H = 232;

const CATEGORY_FILTERS: { value: FontCategoryFilter; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'sans-serif', label: 'Sans' },
  { value: 'serif', label: 'Serif' },
  { value: 'display', label: 'Display' },
  { value: 'handwriting', label: 'Script' },
  { value: 'monospace', label: 'Mono' },
];

/** The short list under "Recommended" in the default view — `FontPicker`'s, unchanged. */
const RECOMMENDED_IDS = [
  'inter',
  'roboto',
  'poppins',
  'montserrat',
  'playfair-display',
  'open-sans',
  'dm-sans',
  'space-grotesk',
];

/**
 * One row of the list.
 *
 * A font the catalogue stocks carries its `FontMeta`; one it does not — a system family the artifact's
 * own CSS names, `Georgia`, `system-ui` — carries only a name. The second kind can still be CHOSEN (it
 * is already rendering in the document, so it plainly works there); it just has no stylesheet to write
 * and no weights to report.
 */
type Row =
  | { kind: 'header'; label: string }
  | { kind: 'font'; name: string; meta: FontMeta | null };

export interface TextFontPanelProps {
  /** The lead family the text is set in right now — the row that opens ticked and centred. */
  family: string;
  /**
   * The families the DOCUMENT already has, from the bridge.
   *
   * They lead the list, because they are the fonts a pick costs nothing for: already loaded, already
   * rendering, no stylesheet to add and nothing to wait for.
   */
  documentFamilies?: string[];
  /**
   * A family, and the stylesheet the artifact needs to have that face — null when it already has it,
   * or when the family is not one the catalogue can serve.
   */
  onPick: (family: string, href: string | null) => void;
  theme?: HeaderTheme;
  className?: string;
}

/** Case- and quote-insensitive, because a computed style says `"Playfair Display"` and a catalogue
 *  entry says `Playfair Display`. */
const normalize = (name: string) => name.replace(/['"]/g, '').trim().toLowerCase();

/**
 * THE FONT PICKER, on the artifact's text toolbar.
 *
 * Ported from `Toolbar/PropertiesPanel/FontPicker` — the windowing, the search, the category chips, the
 * source sections and the per-row lazy loading are all its, and its comments carry the reasoning for
 * each. What is new here is the top section and what a pick actually does.
 *
 * ## Why the document's own fonts lead the list
 *
 * The family menu used to be ONLY those, and that was not conservatism: an artifact is a sandboxed
 * document on an opaque origin, so a font the app loads on its own side is not a font that document can
 * set. Offering the catalogue without doing anything about that would have put ~1,900 names in a menu
 * where all but two fell back in silence.
 *
 * So the pick loads the font — see the host's `onPick`, which posts the stylesheet into the live frame
 * and writes it into the artifact's stored source on the same commit. The document's own fonts still
 * lead, because they are the ones that need none of that.
 *
 * ## The preview is the row, not the artifact
 *
 * `FontPicker` previews a hovered font live on the canvas, which it can do because the canvas is in the
 * same document. Here it would be a message across an origin boundary per pointer-move, and a stylesheet
 * fetch for every name you skim past. The row set in its own face is the preview.
 */
export function TextFontPanel({
  family,
  documentFamilies,
  onPick,
  theme: themeProp,
  className,
}: TextFontPanelProps) {
  const theme = useV15Theme(themeProp);
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<FontCategoryFilter>('all');
  const [scrollTop, setScrollTop] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const current = normalize(family);

  // The current family, so the ticked row is set in itself even when it is nowhere near the window.
  useEffect(() => {
    loadFontByFamily(family);
  }, [family]);

  const documentRows = useMemo<Row[]>(() => {
    const seen = new Set<string>();
    const rows: Row[] = [];
    for (const name of documentFamilies ?? []) {
      const key = normalize(name);
      if (!key || seen.has(key)) continue;
      seen.add(key);
      rows.push({ kind: 'font', name, meta: getFontByFamily(name) ?? null });
    }
    return rows;
  }, [documentFamilies]);

  /**
   * The row list.
   *
   * Any search or category filter collapses to one flat run of results — sections are a way of reading
   * a list nobody asked a question of, and the moment there is a query the answer IS the order.
   */
  const rows = useMemo<Row[]>(() => {
    const asRow = (meta: FontMeta): Row => ({ kind: 'font', name: meta.label, meta });

    if (query.trim() || category !== 'all') {
      const hits = filterFonts({ query, category }).map(asRow);
      // A system family the catalogue does not stock still answers a search for its own name, or the
      // font the text is CURRENTLY in could vanish from its own picker the moment you typed.
      const q = query.trim().toLowerCase();
      const extras = documentRows.filter(
        (row) =>
          row.kind === 'font' &&
          !row.meta &&
          (!q || row.name.toLowerCase().includes(q)) &&
          category === 'all',
      );
      return [...extras, ...hits];
    }

    const out: Row[] = [];
    if (documentRows.length > 0) {
      out.push({ kind: 'header', label: 'In this artifact' });
      out.push(...documentRows);
    }
    const recommended = RECOMMENDED_IDS.map((id) => getFontById(id)).filter(Boolean) as FontMeta[];
    out.push({ kind: 'header', label: 'Recommended' });
    recommended.forEach((meta) => out.push(asRow(meta)));
    const fontshare = FONT_CATALOG.filter((f) => f.source === 'fontshare');
    if (fontshare.length > 0) {
      out.push({ kind: 'header', label: 'Fontshare' });
      fontshare.forEach((meta) => out.push(asRow(meta)));
    }
    out.push({ kind: 'header', label: 'Google Fonts' });
    FONT_CATALOG.filter((f) => f.source === 'google').forEach((meta) => out.push(asRow(meta)));
    return out;
  }, [query, category, documentRows]);

  /**
   * OPEN ON THE CURRENT FAMILY. The panel is mounted only while the menu is open, so mount IS open —
   * there is no `open` flag to key this on, which is one thing simpler than `FontPicker`.
   *
   * The list is scrolled by writing `scrollTop` directly as well as through state: the state drives
   * which rows are rendered, the DOM write is what actually moves the box, and doing only the first
   * would render the right band at the wrong offset for a frame.
   */
  useLayoutEffect(() => {
    const index = tickedIndex;
    const target = index > 0 ? Math.max(0, index * ROW_H - LIST_H / 2 + ROW_H / 2) : 0;
    setScrollTop(target);
    if (listRef.current) listRef.current.scrollTop = target;
    // Deliberately mount-only: re-aiming on every filter would fight the user's own scrolling.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * WHICH ROW WEARS THE TICK, when the same font is in the list more than once.
   *
   * Duplication is inherent and not a bug: Inter is in "In this artifact", in "Recommended" and again
   * under "Google Fonts", because each section answers a different question. Two ticks in one list is
   * a bug though — it reads as the panel being unsure. The first occurrence wins, which is also the
   * one the list opens scrolled to.
   */
  const tickedIndex = useMemo(
    () => rows.findIndex((row) => row.kind === 'font' && normalize(row.name) === current),
    [rows, current],
  );

  const start = Math.max(0, Math.floor(scrollTop / ROW_H) - OVERSCAN);
  const end = Math.min(rows.length, Math.ceil((scrollTop + LIST_H) / ROW_H) + OVERSCAN);
  const visible = rows.slice(start, end);

  // Fetch the faces for a band beyond the rendered window, so names are set in themselves rather than
  // swapping under the eye as you scroll onto them.
  useEffect(() => {
    const from = Math.max(0, start - PRELOAD);
    const to = Math.min(rows.length, end + PRELOAD);
    for (let i = from; i < to; i++) {
      const row = rows[i];
      if (row.kind === 'font' && row.meta) loadFont(row.meta);
    }
  }, [start, end, rows]);

  const resetScroll = () => {
    setScrollTop(0);
    if (listRef.current) listRef.current.scrollTop = 0;
  };

  const pick = (row: Extract<Row, { kind: 'font' }>) => {
    // Null href for a family the document already has and for one the catalogue cannot serve. The host
    // writes a stylesheet only when there is one worth writing.
    const already = documentRows.some((d) => d.kind === 'font' && normalize(d.name) === normalize(row.name));
    onPick(row.name, already ? null : fontHref(row.meta));
  };

  const firstFont = () => rows.find((row) => row.kind === 'font') as Extract<Row, { kind: 'font' }> | undefined;

  return (
    <div className={cn(themes[theme], styles.panel, className)} role="group" aria-label="Font">
      <div className={styles.search}>
        <DeckIcon name="search" size={14} className={styles.searchGlyph} />
        <input
          ref={inputRef}
          className={styles.searchInput}
          type="text"
          spellCheck={false}
          autoComplete="off"
          placeholder="Search fonts"
          aria-label="Search fonts"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            resetScroll();
          }}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              const first = firstFont();
              if (first) pick(first);
              return;
            }
            if (event.key === 'Escape' && query !== '') {
              // Clear the search without shutting the panel. The bar's own Escape would otherwise take
              // this and close the menu out from under someone mid-word.
              event.preventDefault();
              event.stopPropagation();
              setQuery('');
              resetScroll();
            }
          }}
          /**
           * THE ONE CONTROL ON THIS BAR ALLOWED TO TAKE FOCUS, the same exception `TextColorPanel`'s hex
           * field carries and for the same reason: the toolbar root kills mousedown's default so nothing
           * out here can blur the caret inside the artifact's iframe, and a search field you cannot focus
           * is a search field you cannot type in.
           *
           * Losing the caret to it is harmless — the bridge commits on blur, the element stays SELECTED,
           * and every control on this bar addresses the element rather than the caret.
           */
          onMouseDown={(event) => event.stopPropagation()}
        />
      </div>

      <div className={styles.chips} role="group" aria-label="Category">
        {CATEGORY_FILTERS.map((chip) => (
          <button
            key={chip.value}
            type="button"
            className={cn(styles.chip, category === chip.value && styles.chipOn)}
            aria-pressed={category === chip.value}
            onClick={() => {
              setCategory(chip.value);
              resetScroll();
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      <div
        ref={listRef}
        className={styles.list}
        style={{ height: LIST_H }}
        onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
        role="listbox"
        aria-label="Fonts"
        tabIndex={-1}
      >
        {rows.length === 0 ? (
          <p className={styles.empty}>No font matches that.</p>
        ) : (
          <div className={styles.spacer} style={{ height: rows.length * ROW_H }}>
            {visible.map((row, offset) => {
              const index = start + offset;
              const top = index * ROW_H;
              if (row.kind === 'header') {
                return (
                  <div
                    key={`row-${index}`}
                    className={styles.section}
                    style={{ top, height: ROW_H } as React.CSSProperties}
                  >
                    {row.label}
                  </div>
                );
              }
              const ticked = index === tickedIndex;
              return (
                <button
                  key={`row-${index}`}
                  type="button"
                  role="option"
                  // `ticked`, not `selected`: a single-select listbox may have exactly one selected
                  // option, and the same family legitimately appears in two or three sections. The
                  // first occurrence is the one that carries it, in the tree as well as on screen.
                  aria-selected={ticked}
                  className={styles.row}
                  style={{ top, height: ROW_H } as React.CSSProperties}
                  onClick={() => pick(row)}
                >
                  {/* The name set in ITSELF, which is the whole point of a font list. The runtime value
                      is the family, so it is the one thing here that has to be an inline style. */}
                  <span
                    className={styles.name}
                    style={{ fontFamily: row.meta ? row.meta.family : `'${row.name}'` }}
                  >
                    {row.name}
                  </span>
                  {ticked ? (
                    <span className={styles.tick} aria-hidden>
                      <DeckIcon name="check" size={14} />
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
